import {Indicator, IndicatorResponse} from "@/types/models";
import {createContext, Dispatch, PropsWithChildren, useContext, useState} from "react";
import {useSessionMetadataContext} from "@/Context/SessionMetadataContext";
import {useNodeNavigationContext} from "@/Context/NodeNavigationContext";
import {useProgressContext} from "@/Context/ProgressContext";
import {postResponse} from "@/services/SurveyService";

export type EvaluatingIndicator = {
    indicator: Indicator
    nodeIndex: number
}

type ResponseContextProps = {
    respond: (indicator: Indicator, nodeIndex: number | null | undefined, input: string, asText?: boolean, assumedFromResponseId?: number) => Promise<IndicatorResponse>
    lastResponse: IndicatorResponse | undefined
    evaluatingIndicator: EvaluatingIndicator | undefined
    setEvaluatingIndicator: Dispatch<EvaluatingIndicator | undefined>
}

const ResponseContext = createContext<ResponseContextProps | undefined>(undefined)

export const ResponseContextProvider = ({children}: PropsWithChildren) => {
    const {session, rootNode, mode} = useSessionMetadataContext()
    const {findIndicator, updateNodesText, nodes} = useNodeNavigationContext()
    const {fetchProgressSummary} = useProgressContext()

    const [lastResponse, setLastResponse] = useState<IndicatorResponse | undefined>(undefined)
    const [evaluatingIndicator, setEvaluatingIndicator] = useState<EvaluatingIndicator | undefined>(undefined)

    const findAssumers = (indicator: Indicator, node?: typeof rootNode, nodeIndex: number = 0, acc: Array<Indicator> = []): Indicator[] => {
        if (!node) node = rootNode!

        let assumers = node.content.filter(c =>
            c._class === 'Indicator' && c.assume_from_indicator_id === indicator.id
            && !acc.find(a => a.id === c.id)
        )
        for (const assumer of assumers) {
            if (!assumer._temp) assumer._temp = {}
            assumer._temp.array_index = nodeIndex
        }

        if (node.children) {
            for (const child of node.children) {
                if (child.array_index === nodeIndex + 1) {
                    assumers = [...assumers, ...findAssumers(indicator, child, nodeIndex + 1, acc)]
                } else if (!child.array_max) {
                    assumers = [...assumers, ...findAssumers(indicator, child, nodeIndex, acc)]
                }
            }
        }

        return assumers as Indicator[]
    }

    const respond = async (indicator: Indicator, nodeIndex: number | null | undefined, input: string, asText?: boolean, assumedFromResponseId: number | undefined = undefined) => {
        const promise = postResponse(session!, {
            indicator_id: indicator.id,
            node_index: nodeIndex,
            input: asText ? undefined : input,
            input_text: asText ? input : undefined,
            assumed_from_response_id: assumedFromResponseId,
            evaluation: mode === 'evaluate' ? true : undefined
        });

        promise.then((response: IndicatorResponse) => {
            indicator.response = response

            setLastResponse(response)
            fetchProgressSummary()

            const assumers = findAssumers(indicator, rootNode!, nodeIndex ?? 0)
            for (const assumer of assumers) {
                respond(assumer, assumer._temp.array_index, input, asText, response.id)
            }

            updateNodesText(nodes)
        }).catch((error: Error) => {
            console.warn(`Response failed: ${error.message}`)
            throw (error)
        })

        return promise;
    }

    return <ResponseContext.Provider value={{
        respond,
        lastResponse,
        evaluatingIndicator,
        setEvaluatingIndicator,
    }}>
        {children}
    </ResponseContext.Provider>
}

export function useResponseContext(): ResponseContextProps {
    const context = useContext(ResponseContext);
    if (!context) throw new Error("No ResponseContext is defined")
    return context
}
